// src/merch/OrderDialog.jsx
//
// Order summary → quantity → rights checkbox → Stripe. Prices here are
// display-only; the server re-prices everything.

import { href } from "./stores";
import React, { useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { merch, layersForSide } from "./store";
import { getSides } from "./products";
import { placeOrder } from "./checkout";
import { trademarkHits } from "./flags";

export default function OrderDialog({ product, size, colour, onClose }) {
  const snap = useSnapshot(merch);
  const [qty, setQty] = useState(1);
  const [rights, setRights] = useState(false);
  const [marketing, setMarketing] = useState(false); // CASL: separate, unchecked opt-in
  const [step, setStep] = useState(null);
  const [error, setError] = useState(null);

  const sides = getSides(product, size, snap.hand);
  const printSides = sides.filter((s) =>
    (s.id === snap.side ? snap.layers : snap.sideLayers[s.id] || []).length || (s.id === snap.side && snap.paint.strokes)
  );
  const backExtra = printSides.some((s) => s.id === "back") ? product.backSidePrice || 0 : 0;
  const unit = product.price + backExtra;

  const hits = useMemo(() => {
    const texts = [];
    for (const s of sides) for (const L of layersForSide(s.id)) if (L.kind === "text") texts.push(L.text?.text || "");
    return trademarkHits(texts);
  }, [snap.rev]); // eslint-disable-line react-hooks/exhaustive-deps

  async function go() {
    setError(null);
    try {
      await placeOrder({ product, size, colour, marketing, quantity: qty, rightsConfirmed: rights, onStep: setStep });
    } catch (e) {
      setError(e.message || String(e));
      setStep(null);
    }
  }

  const busy = !!step;

  return (
    <div className="ma-modal" onPointerDown={(e) => !busy && e.target === e.currentTarget && onClose()}>
      <div className="ma-modal-card ma-order" role="dialog" aria-label="Order">
        <div className="ma-modal-head">
          <b>Your order</b>
          <button className="ma-btn ghost" onClick={onClose} disabled={busy} aria-label="Close">✕</button>
        </div>

        <div className="ma-order-sum">
          <div><span>Product</span><b>{product.name} · {size.label}</b></div>
          <div><span>Colour</span><b><i className="ma-order-dot" style={{ background: colour.hex }} />{colour.label}</b></div>
          {product.handed && <div><span>Shot</span><b>{snap.hand === "left" ? "Left" : "Right"}</b></div>}
          <div><span>Printed</span><b>{printSides.map((s) => s.label).join(" + ") || "—"}</b></div>
          <div>
            <span>Quantity</span>
            <span className="ma-qty">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={busy || qty <= 1} aria-label="Fewer">−</button>
              <input
                inputMode="numeric" value={qty} disabled={busy}
                onChange={(e) => setQty(Math.max(1, Math.min(99, Number(e.target.value.replace(/\D/g, "")) || 1)))}
              />
              <button onClick={() => setQty((q) => Math.min(99, q + 1))} disabled={busy || qty >= 99} aria-label="More">+</button>
            </span>
          </div>
          <div className="ma-order-total"><span>Subtotal</span><b>${(unit * qty).toFixed(2)}</b></div>
          <p className="ma-muted">Shipping and tax are added at checkout.</p>
        </div>

        {hits.length > 0 && (
          <div className="ma-note warn">
            Heads up: your text mentions <b>{hits.join(", ")}</b>. We can't print pro team or brand names and logos,
            so this design may be declined. (Your own team's name is totally fine!)
          </div>
        )}

        <div className="ma-note">
          🏒 <b>We check every design by hand.</b> Your card is only <b>authorized</b> now and charged once we approve it.
          If we can't print it, the hold is released and you pay nothing.
        </div>

        <label className="ma-check">
          <input type="checkbox" checked={rights} disabled={busy} onChange={(e) => setRights(e.target.checked)} />
          <span>I own or have permission to use everything in this design — photos, logos and names.</span>
        </label>

        <label className="ma-check">
          <input type="checkbox" checked={marketing} disabled={busy} onChange={(e) => setMarketing(e.target.checked)} />
          <span>
            Send me MerchMod news, new designs and team deals (optional — unsubscribe any time).{" "}
            <a href={href("/privacy")} target="_blank" rel="noreferrer">Privacy</a>
          </span>
        </label>

        {error && <div className="ma-note error">{error}</div>}

        <div className="ma-modal-foot">
          <button className="ma-btn primary" disabled={!rights || busy || !printSides.length} onClick={go}>
            {step || "Continue to secure checkout"}
          </button>
        </div>
      </div>
    </div>
  );
}
